import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import PublicNav from "../../components/PublicNav";
import Footer from "../../components/Footer";
import { Gift, CreditCard } from "@phosphor-icons/react";

const FIXED_AMOUNTS = [75, 150, 225, 300];

export default function GiftCards() {
  const [params] = useSearchParams();
  const contextTourSlug = params.get("tour");
  const [contextTour, setContextTour] = useState(null);
  const [amount, setAmount] = useState(150);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({
    sender_name: "", sender_email: "",
    recipient_name: "", recipient_email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // If the user came from a specific tour page, fetch its title + preset a suggested amount.
  useEffect(() => {
    if (!contextTourSlug) return;
    api.get(`/tours/${contextTourSlug}`).then(r => {
      setContextTour(r.data);
      if (r.data?.base_price_per_guest) {
        // Suggest a 2-guest bundle by default — most-common gift size.
        const suggested = Math.round(r.data.base_price_per_guest * 2);
        const closestFixed = FIXED_AMOUNTS.reduce((p, c) => Math.abs(c - suggested) < Math.abs(p - suggested) ? c : p);
        setAmount(closestFixed);
      }
    }).catch(() => {});
  }, [contextTourSlug]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const effectiveAmount = () => {
    if (custom && Number(custom) > 0) return Number(custom);
    return amount;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const amt = effectiveAmount();
    if (amt < 25 || amt > 10000) { setError("Amount must be between $25 and $10,000."); return; }
    if (!form.sender_name || !form.sender_email || !form.recipient_name || !form.recipient_email) {
      setError("Please complete sender and recipient details."); return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/gift-cards/purchase", {
        amount: amt,
        sender_name: form.sender_name,
        sender_email: form.sender_email,
        recipient_name: form.recipient_name,
        recipient_email: form.recipient_email,
        message: form.message,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not start checkout.");
      setSubmitting(false);
    }
  };

  return (
    <div className="public-shell">
      <PublicNav />
      <section className="bg-ms-cream pt-32 pb-20 md:pt-40 md:pb-28" data-testid="gift-cards-page">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="label-eyebrow text-ms-brass flex items-center gap-2"><Gift size={14} weight="duotone" /> A gift that's actually different</p>
          <h1 className="serif text-ms-ink text-5xl md:text-6xl mt-4 leading-[0.95]">Mystery Served gift certificates.</h1>
          {contextTour && (
            <p className="mt-4 inline-block px-3 py-1.5 bg-ms-brass/10 border border-ms-brass/30 rounded-full text-ms-brass text-xs label-eyebrow" data-testid="gift-context-tour">
              Gifting toward → {contextTour.title}
            </p>
          )}
          <p className="mt-6 text-ms-stone text-lg leading-relaxed max-w-2xl">
            {contextTour
              ? `Send someone the ${contextTour.title} experience — or any Mystery Served tour. Codes arrive by email instantly and are good for 12 months.`
              : "Give a friend, family, or team an immersive mystery adventure across St. Petersburg. Codes arrive instantly by email and can be applied at checkout for any Mystery Served tour. Valid for 12 months."}
          </p>

          <form onSubmit={submit} className="mt-10 bg-white border border-ms-edge rounded-md p-6 md:p-8 space-y-6" data-testid="gift-cards-form">
            {/* Amount */}
            <div>
              <p className="label-eyebrow text-ms-stone text-xs">Choose an amount</p>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                {FIXED_AMOUNTS.map(a => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => { setAmount(a); setCustom(""); }}
                    className={`py-3 text-sm rounded-md border-2 transition-colors ${amount === a && !custom ? "border-ms-brass bg-ms-brass/10 text-ms-ink" : "border-ms-edge text-ms-stone hover:border-ms-brass"}`}
                    data-testid={`gc-amount-${a}`}
                  >
                    ${a}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm text-ms-stone">or custom:</span>
                <span className="text-ms-stone">$</span>
                <input
                  type="number"
                  min="25"
                  max="10000"
                  value={custom}
                  onChange={e => setCustom(e.target.value)}
                  placeholder="0"
                  className="w-32 border border-ms-edge rounded-md px-3 py-2 text-sm"
                  data-testid="gc-custom-amount"
                />
              </div>
            </div>

            <div className="brass-line" />

            {/* Sender */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Your name">
                <input required value={form.sender_name} onChange={e => upd("sender_name", e.target.value)} className="input" data-testid="gc-sender-name" />
              </Field>
              <Field label="Your email">
                <input required type="email" value={form.sender_email} onChange={e => upd("sender_email", e.target.value)} className="input" data-testid="gc-sender-email" />
              </Field>
            </div>

            {/* Recipient */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Recipient's name">
                <input required value={form.recipient_name} onChange={e => upd("recipient_name", e.target.value)} className="input" data-testid="gc-recipient-name" />
              </Field>
              <Field label="Recipient's email">
                <input required type="email" value={form.recipient_email} onChange={e => upd("recipient_email", e.target.value)} className="input" data-testid="gc-recipient-email" />
              </Field>
            </div>

            <Field label="Personal message (optional)">
              <textarea
                value={form.message}
                onChange={e => upd("message", e.target.value)}
                rows={3}
                maxLength={600}
                placeholder="Happy birthday — go solve a mystery!"
                className="input"
                data-testid="gc-message"
              />
            </Field>

            {error && <p className="text-ms-danger text-sm" data-testid="gc-error">{error}</p>}

            <div className="flex items-center justify-between pt-2">
              <div>
                <p className="text-ms-stone text-sm">Total</p>
                <p className="serif text-3xl text-ms-ink">${effectiveAmount().toFixed(2)}</p>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="gc-submit"
              >
                <CreditCard size={16} /> {submitting ? "Redirecting…" : "Continue to payment"}
              </button>
            </div>
            <p className="text-xs text-ms-stone">Secure checkout via Stripe. Your code will be emailed to the recipient immediately after payment clears.</p>
          </form>
        </div>
      </section>
      <Footer />
      <style>{`.input{width:100%; border:1px solid #E4DBC7; border-radius:6px; padding:10px 12px; font-size:14px;} .input:focus{outline:none; border-color:#B8753F;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow text-ms-stone text-xs">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
